import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, Check } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import { DateTimeText, Id } from '../../components/ui/Mono';
import { SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import { FormatLocationPlain } from '../../components/warehouse/collector/collectorScan';
import IncidentDetailModal, {
    IncidentLocationOf,
    IncidentPersonText,
    IncidentReporterOf,
} from '../../components/warehouse/IncidentDetailModal';
import { ExtractListRows } from '../../components/warehouse/payload';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const STATUS_OPEN = 'OPEN';
const STATUS_RESOLVED = 'RESOLVED';
const STATUS_FILTERS = [STATUS_OPEN, STATUS_RESOLVED];

const BLANK_CELL = <span className="text-mono-400">—</span>;

// Cor por gravidade do que o incidente significa para o estoque: ocupação não
// cadastrada e pallet ausente são divergência de INVENTÁRIO (o sistema mente
// sobre o chão); bipe errado é ruído de operação.
const KIND_VARIANT = {
    UNREGISTERED_OCCUPANCY: 'danger',
    MISSING_PALLET: 'danger',
    OCCUPIED_DESTINATION: 'warning',
    WO_CANCELED_WITH_LOAD: 'warning',
    SCAN_MISMATCH: 'info',
    OTHER: 'neutral',
};

/**
 * /warehouse/incidents — PAINEL DE INCIDENTES (Supervisor).
 *
 * ── Por que é uma PÁGINA e não uma aba das Configurações ────────────────────
 * O contrato do M4 deixou a escolha para o agente. Virou página por quatro
 * motivos:
 *
 * 1. Configurações do Armazém é um console de CADASTRO — a própria subtítulo
 *    da página diz isso ("cadastros do módulo"). Incidente não é cadastro: é
 *    EVENTO com ciclo de vida (OPEN → RESOLVED), uma fila de trabalho que
 *    nasce sozinha, do chão, enquanto ninguém está olhando. Fila de trabalho
 *    escondida atrás de um menu chamado "Configurações" é fila não atendida.
 * 2. A rota `/warehouse` é guardada por um `anyOf` das permissões de cadastro
 *    e a página deriva as abas de `TAB_IDS` × `wms:manage:{id}`. Encaixar
 *    incidentes ali obrigaria a alargar aquele guarda com uma permissão que
 *    não é de cadastro — e um Supervisor que só tenha `wms:manage:incidents`
 *    cairia numa tela intitulada "Configurações do Armazém" com uma aba só.
 * 3. Endereço próprio é compartilhável: o incidente aberto pelo coletor vira
 *    conversa ("vê o incidente da R02-B10") e um link resolve isso; um índice
 *    de aba, não.
 * 4. Precedente do próprio módulo: Localizações e LPNs — os outros dois
 *    consoles OPERACIONAIS — já são páginas próprias, com item de menu e
 *    permissão granular própria. Incidentes tem exatamente essa forma.
 *
 * ── O ajuste do aceite do M4 (histórico completo) ───────────────────────────
 * A página continua sendo a página; o que mudou foi a densidade de informação,
 * porque quem audita meses depois não tem a memória de quem estava lá:
 *   - a TABELA responde sem abrir nada — tipo, status, posição (com o texto do
 *     endereço), LPN, quem abriu, quando, QUANTAS anotações o caso tem e quem
 *     resolveu;
 *   - a linha inteira abre o HISTÓRICO (IncidentDetailModal): abertura →
 *     anotações em ordem → resolução;
 *   - resolver deixou de ser um atalho da linha e passou a acontecer DENTRO do
 *     histórico, com nota final obrigatória — encerrar um caso sem ler o que
 *     já foi registrado era o jeito mais fácil de perder informação.
 */
const WarehouseIncidentsPage = () => {
    const { label } = useWarehouseLabels();
    const { checkPermission } = useAuth();
    const [status, setStatus] = useState(STATUS_OPEN);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // `{id, resolving}` — a MESMA tela de histórico serve para ler e para
    // resolver; `resolving` só decide se o campo da nota final já vem aberto.
    const [detail, setDetail] = useState(null);

    const canResolve = checkPermission('wms:manage:incidents');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await api.getWarehouseIncidents({ status });
            setRows(ExtractListRows(data, ['incidents']));
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [label, status]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(
        () => [
            {
                key: 'kind',
                label: label('wms.incidents.field.kind'),
                getValue: (row) => label(`wms.incident_kind.${row.kind}`),
                render: (row) => (
                    <Badge variant={KIND_VARIANT[row.kind] || 'neutral'}>{label(`wms.incident_kind.${row.kind}`)}</Badge>
                ),
            },
            {
                key: 'status',
                label: label('wms.incidents.field.status'),
                getValue: (row) => label(`wms.incident_status.${row.status}`),
                render: (row) => (
                    <Badge variant={row.status === STATUS_RESOLVED ? 'success' : 'warning'}>
                        {label(`wms.incident_status.${row.status}`)}
                    </Badge>
                ),
            },
            {
                key: 'location',
                label: label('wms.incidents.field.location'),
                getValue: (row) => row.location_code || '',
                render: (row) => {
                    const location = IncidentLocationOf(row);
                    if (!location) {
                        return BLANK_CELL;
                    }
                    const plain = FormatLocationPlain(location, { label });
                    return (
                        <div className="min-w-0">
                            <Id value={row.location_code} className="text-xs font-medium" />
                            {plain && plain !== row.location_code ? (
                                <p className="truncate text-xs text-mono-500 dark:text-mono-400">{plain}</p>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                key: 'lpn',
                label: label('wms.incidents.field.lpn'),
                getValue: (row) => row.lpn_code || '',
                render: (row) => (row.lpn_code ? <Id value={row.lpn_code} className="text-xs" /> : BLANK_CELL),
            },
            {
                key: 'created_by',
                label: label('wms.incidents.field.created_by'),
                // A PESSOA do crachá quando o incidente veio do coletor (conta
                // compartilhada de dispositivo), a conta quando veio do console
                // — a MESMA regra da ficha, por `IncidentReporterOf`.
                getValue: (row) => IncidentReporterOf(row)?.name || '',
                render: (row) => {
                    const reporter = IncidentReporterOf(row);
                    return reporter ? (
                        <IncidentPersonText person={reporter} className="text-sm" />
                    ) : (
                        <span className="text-sm">—</span>
                    );
                },
            },
            {
                key: 'created_at',
                label: label('wms.incidents.field.created_at'),
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative className="text-xs" />,
            },
            {
                // Quantas anotações o caso tem: é o sinal de que houve condução
                // (e o convite para abrir o histórico) sem precisar entrar em
                // cada incidente para descobrir.
                key: 'notes_count',
                label: label('wms.incidents.field.notes_count'),
                getValue: (row) => Number(row.notes_count || 0),
                render: (row) =>
                    Number(row.notes_count || 0) > 0 ? (
                        <Badge variant="neutral">{Number(row.notes_count)}</Badge>
                    ) : (
                        BLANK_CELL
                    ),
            },
            {
                key: 'resolved_by',
                label: label('wms.incidents.field.resolved_by'),
                getValue: (row) => row.resolved_by_name || '',
                render: (row) =>
                    row.status === STATUS_RESOLVED ? (
                        <span className="flex flex-col gap-0.5">
                            <span className="text-sm">{row.resolved_by_name || row.resolved_by || '—'}</span>
                            {row.resolved_at ? (
                                <DateTimeText value={row.resolved_at} relative className="text-[11px] text-mono-500" />
                            ) : null}
                        </span>
                    ) : (
                        BLANK_CELL
                    ),
            },
            {
                key: 'report',
                label: label('wms.incidents.field.report'),
                getValue: (row) => row.notes || '',
                render: (row) =>
                    row.notes ? (
                        <span
                            className="block max-w-[280px] truncate text-sm text-mono-600 dark:text-mono-400"
                            title={row.notes}
                        >
                            {row.notes}
                        </span>
                    ) : (
                        BLANK_CELL
                    ),
            },
            {
                key: 'actions',
                label: label('wms.common.actions'),
                sortable: false,
                exportable: false,
                render: (row) => {
                    const resolving = row.status === STATUS_OPEN && canResolve;
                    return (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                setDetail({ id: row.id, resolving });
                            }}
                        >
                            {resolving ? (
                                <>
                                    <Check size={14} className="mr-1.5" />
                                    {label('wms.incidents.resolve')}
                                </>
                            ) : (
                                label('wms.incidents.open_detail')
                            )}
                        </Button>
                    );
                },
            },
        ],
        [canResolve, label],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title={label('wms.incidents.title')}
                subtitle={label('wms.incidents.description')}
                actions={
                    <Button variant="secondary" onClick={load} loading={loading}>
                        <ArrowClockwise size={18} className="mr-2" />
                        {label('wms.common.retry')}
                    </Button>
                }
            />

            {/* Filtro OPEN/RESOLVED — o `status` vai no wire (snake_case),
                não é recorte de uma lista já carregada. */}
            <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((option) => {
                    const isActive = option === status;
                    return (
                        <button
                            key={option}
                            type="button"
                            onClick={() => setStatus(option)}
                            aria-pressed={isActive}
                            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                                isActive
                                    ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                    : 'border-mono-300 bg-white text-mono-700 hover:bg-mono-50 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300 dark:hover:bg-mono-900'
                            }`}
                        >
                            {label(`wms.incident_status.${option}`)}
                        </button>
                    );
                })}
            </div>

            {loadError ? (
                <ErrorCard
                    title={label('wms.incidents.title')}
                    message={loadError}
                    action={label('wms.common.retry')}
                    actionFn={load}
                />
            ) : loading ? (
                <SkeletonTable rows={6} />
            ) : (
                <>
                    <StandardTable
                        rows={rows}
                        columns={columns}
                        rowKey={(row, index) => row?.id ?? index}
                        countLabel={label('wms.incidents.count_label')}
                        exportFileName="wms-incidentes"
                        emptyMessage={
                            status === STATUS_OPEN
                                ? label('wms.incidents.empty_open')
                                : label('wms.incidents.empty_resolved')
                        }
                        searchPlaceholder={label('wms.incidents.search_placeholder')}
                        onRowClick={(row) => setDetail(row?.id ? { id: row.id, resolving: false } : null)}
                    />
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.incidents.detail_hint')}</p>
                </>
            )}

            {detail ? (
                <IncidentDetailModal
                    incidentId={detail.id}
                    label={label}
                    kindVariant={KIND_VARIANT[rows.find((row) => row.id === detail.id)?.kind] || 'neutral'}
                    canManage={canResolve}
                    startResolving={detail.resolving}
                    onClose={() => setDetail(null)}
                    onChanged={load}
                />
            ) : null}
        </div>
    );
};

export default WarehouseIncidentsPage;
